import { fetchApi } from '@/services/api';

export type EquivalenceUnit = {
  id: number;
  name: string;
  abbreviation?: string | null;
};

export type EquivalenceMember = {
  id: number;
  equivalent_product_id: number;
  equivalent_code: string;
  equivalent_name?: string | null;
  is_active: boolean;
  sales_conversion_factor: string;
  stock_conversion_factor: string;
  conversion_pending: boolean;
  show_in_suggested_order?: boolean;
  sales_unit_id?: number | null;
  stock_unit_id?: number | null;
  sell_unit_id?: number | null;
  sell_unit_name?: string | null;
  sell_unit_abbreviation?: string | null;
  buy_unit_id?: number | null;
  buy_unit_name?: string | null;
  buy_unit_abbreviation?: string | null;
  source: string;
};

export type EquivalenceGroup = {
  canonical_product_id: number;
  canonical_code?: string | null;
  suggestion_scope: string;
  conversion_pending: boolean;
  unification_status: string;
  sales_unification_status?: string;
  stock_unification_status?: string;
  principal_product_id?: number | null;
  members: EquivalenceMember[];
  units?: EquivalenceUnit[];
};

export type EquivalenceUnificationPreview = {
  canonical_product_id: number;
  principal_product_id: number;
  principal_code?: string | null;
  scope?: string;
  preview_token: string;
  sales_truncated?: boolean;
  sales_preview: Array<{
    alias_code: string;
    date: string;
    warehouse_id: number | null;
    qty_alias: string;
    qty_principal_ceiled: string;
    qty_total?: string;
    is_principal?: boolean;
  }>;
  stock_preview: Array<{
    alias_code: string;
    warehouse_id: number | null;
    qty_alias: string;
    qty_principal_ceiled: string;
    qty_total?: string;
    is_principal?: boolean;
  }>;
  summary?: {
    alias_count: number;
    sales_samples: number;
    stock_lines: number;
    nothing_to_move: boolean;
    sales_unification_status?: string;
    stock_unification_status?: string;
  };
};

export type UnifyScope = 'sales' | 'stock' | 'both';

export const equivalencesService = {
  async list(): Promise<EquivalenceGroup[]> {
    const res = await fetchApi<{ results: EquivalenceGroup[] }>('products/equivalences/');
    return res.results || [];
  },
  async create(body: Record<string, unknown>) {
    return fetchApi('products/equivalences/', {
      method: 'POST',
      body,
    });
  },
  async patch(canonicalProductId: number, body: Record<string, unknown>) {
    return fetchApi(`products/equivalences/${canonicalProductId}/`, {
      method: 'PATCH',
      body,
    });
  },
  async dissolve(canonicalProductId: number, opts?: { revertUnification?: boolean }) {
    const q = opts?.revertUnification ? '?revert_unification=true' : '';
    return fetchApi<{
      canonical_product_id: number;
      rows_deleted: number;
      had_unification: boolean;
      reverted_unification?: boolean;
      undo?: unknown;
    }>(`products/equivalences/${canonicalProductId}/${q}`, {
      method: 'DELETE',
    });
  },
  async removeMember(canonicalProductId: number, memberId: number) {
    return fetchApi<{
      canonical_product_id: number;
      member_id: number;
      equivalent_code?: string;
      rows_deleted: number;
      group_dissolved: boolean;
    }>(`products/equivalences/${canonicalProductId}/?member_id=${memberId}`, {
      method: 'DELETE',
    });
  },
  async previewUnification(canonicalProductId: number, scope: UnifyScope = 'both') {
    return fetchApi<EquivalenceUnificationPreview>(
      `products/equivalences/${canonicalProductId}/unification/preview/`,
      {
        method: 'POST',
        body: { scope },
      }
    );
  },
  async runUnification(
    canonicalProductId: number,
    previewToken: string,
    scope: UnifyScope = 'both'
  ) {
    return fetchApi(`products/equivalences/${canonicalProductId}/unification/run/`, {
      method: 'POST',
      body: { preview_token: previewToken, scope },
    });
  },
  async undoUnification(canonicalProductId: number, scope: UnifyScope = 'both') {
    return fetchApi(`products/equivalences/${canonicalProductId}/unification/undo/`, {
      method: 'POST',
      body: { scope },
    });
  },
};
